import React, {StrictMode, useEffect} from 'react';
import routes from "./routes";
import {createBrowserRouter, RouterProvider,} from "react-router-dom";
import {http} from './utils/http';
import useAuthStore from "./store/auth";
import {useTranslation} from 'react-i18next';
import {QueryClient, QueryClientProvider,} from 'react-query'
import {ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {setWhiteLabelMetadata} from "./utils/whitelabel";
import ConfirmationModal from './components/ConfirmationModal';
import {Sentry, sentryCreateBrowserRouter, setSentryUser} from "./utils/sentry";
import ErrorFallback from "./components/ErrorFallback";

const router = sentryCreateBrowserRouter(routes);
const queryClient = new QueryClient()

function App() {
    const {token, user, merchant, setUserAuth, clearToken, setToken, setAuthChecked} = useAuthStore();
    const {i18n} = useTranslation();

    async function getAccount() {
        try {
            const response = await http.get("user");
            const {user, roles, permissions, merchant, merchants} = await response?.data;
            if (user) {
                setUserAuth({user, roles, permissions, merchant, merchants});
                if (!token) {
                    setToken('session_active');
                }
            } else {
                clearToken();
            }
        } catch (e) {
            console.error(e);
            clearToken();
        } finally {
            setAuthChecked();
        }
    }

    useEffect(() => {
        setSentryUser(user, merchant);
    }, [user, merchant]);

    useEffect(() => {
        if (!user && !window.location.pathname.includes('/impersonate/')) {
            getAccount();
        }
    }, [token]);

    useEffect(() => {
        setWhiteLabelMetadata();
        if (!window.location.pathname.includes('/impersonate/')) {
            getAccount();
        }
    }, []);

    useEffect(() => {
        document.body.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    }, [i18n.language]);

    useEffect(() => {
        const handleReactQueryClientReset = (event) => {
            try {
                if (event.detail?.cacheKey) {
                    queryClient.invalidateQueries(event.detail.cacheKey);
                } else {
                    queryClient.invalidateQueries();
                }
            } catch (error) {
                console.error('Error invalidating queries:', error);
            }
        };
        window.addEventListener('resetReactQueryClient', handleReactQueryClientReset);
        return () => {
            window.removeEventListener('resetReactQueryClient', handleReactQueryClientReset);
        };
    }, []);

    return (
        <StrictMode>
            <Sentry.ErrorBoundary fallback={({ error, resetError }) => (
                <ErrorFallback error={error} resetError={resetError} />
            )}>
                <QueryClientProvider client={queryClient}>
                    <RouterProvider router={router}/>
                    <ToastContainer
                        position="top-right"
                        autoClose={5000}
                        hideProgressBar={false}
                        newestOnTop
                        closeOnClick
                        rtl={i18n.language === 'ar'}
                        pauseOnFocusLoss
                        draggable
                        pauseOnHover
                        theme="light"/>
                    <ConfirmationModal/>
                </QueryClientProvider>
            </Sentry.ErrorBoundary>
        </StrictMode>
    );
}

export default App;
